import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProducts } from '../../services/api';
import { FaBox, FaUsers, FaShoppingCart, FaDollarSign, FaPlus, FaList, FaSignOutAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../services/api';
import { toast } from 'react-toastify';

const AdminDashboard = () => {
    const [stats, setStats] = useState({
        totalProducts: 0,
        totalOrders: 0,
        totalUsers: 0,
        totalRevenue: 0
    });
    const [recentProducts, setRecentProducts] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        fetchStats();
        fetchRecentProducts();
    }, []);

    const fetchStats = async () => {
        try {
            const products = await getProducts();
            setStats({
                totalProducts: products.products.length,
                totalOrders: 124,
                totalUsers: 89,
                totalRevenue: 12450
            });
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchRecentProducts = async () => {
        try {
            const products = await getProducts();
            setRecentProducts(products.products.slice(0, 5));
        } catch (error) {
            console.error('Error fetching recent products:', error);
        }
    };

    const handleLogout = () => {
        logout();
        toast.success('Logged out successfully');
        navigate('/admin/login');
    };

    const statCards = [
        { title: 'Total Products', value: stats.totalProducts, icon: FaBox, color: 'bg-blue-500', change: '+12%' },
        { title: 'Total Orders', value: stats.totalOrders, icon: FaShoppingCart, color: 'bg-green-500', change: '+8%' },
        { title: 'Total Users', value: stats.totalUsers, icon: FaUsers, color: 'bg-purple-500', change: '+5%' },
        { title: 'Revenue', value: `£${stats.totalRevenue}`, icon: FaDollarSign, color: 'bg-yellow-500', change: '+15%' }
    ];

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Admin Header */}
            <div className="bg-white shadow-md">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex justify-between items-center">
                        <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 text-red-600 hover:text-red-700 transition"
                        >
                            <FaSignOutAlt />
                            Logout
                        </button>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-8">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {statCards.map((stat, index) => (
                        <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-gray-600 text-sm">{stat.title}</p>
                                    <p className="text-2xl font-bold mt-2">{stat.value}</p>
                                    <p className="text-green-600 text-xs mt-1">{stat.change} from last month</p>
                                </div>
                                <div className={`${stat.color} p-3 rounded-full text-white`}>
                                    <stat.icon size={24} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Quick Actions & Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Quick Actions */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
                        <div className="space-y-3">
                            <Link 
                                to="/admin/products" 
                                className="flex items-center justify-between w-full bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition group"
                            >
                                <span className="flex items-center gap-2">
                                    <FaPlus />
                                    Add New Product
                                </span>
                                <span className="opacity-0 group-hover:opacity-100 transition">→</span>
                            </Link>
                            <Link 
                                to="/admin/products" 
                                className="flex items-center justify-between w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition group"
                            >
                                <span className="flex items-center gap-2">
                                    <FaList />
                                    Manage Products
                                </span>
                                <span className="opacity-0 group-hover:opacity-100 transition">→</span>
                            </Link>
                            <button className="flex items-center justify-between w-full bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition group">
                                <span className="flex items-center gap-2">
                                    <FaShoppingCart />
                                    View All Orders
                                </span>
                                <span className="opacity-0 group-hover:opacity-100 transition">→</span>
                            </button>
                            <button className="flex items-center justify-between w-full bg-yellow-600 text-white px-4 py-3 rounded-lg hover:bg-yellow-700 transition group">
                                <span className="flex items-center gap-2">
                                    <FaUsers />
                                    Manage Customers
                                </span>
                                <span className="opacity-0 group-hover:opacity-100 transition">→</span>
                            </button>
                        </div>
                    </div>

                    {/* Recent Products */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-semibold mb-4">Recently Added Products</h2>
                        <div className="space-y-3">
                            {recentProducts.length > 0 ? (
                                recentProducts.map((product) => (
                                    <div key={product.id} className="flex items-center justify-between border-b pb-3">
                                        <div>
                                            <p className="font-medium text-gray-800">{product.name}</p>
                                            <p className="text-sm text-gray-500">£{product.price}</p>
                                        </div>
                                        <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded">
                                            In Stock
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 text-center py-4">No products found</p>
                            )}
                        </div>
                        <Link to="/admin/products" className="mt-4 text-purple-600 hover:text-purple-700 text-sm font-medium block text-center">
                            View All Products →
                        </Link>
                    </div>
                </div>

                {/* Recent Orders Table */}
                <div className="mt-8 bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-semibold mb-4">Recent Orders</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                <tr>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#12345</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">John Doe</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">£129.99</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                            Delivered
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2024-01-15</td>
                                </tr>
                                <tr>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#12344</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">Jane Smith</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">£79.99</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                            Processing
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">2024-01-14</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;